// @tennis-matches/services/tournament-wizard
// Generic setup wizard navigation (shared by Schleifchen + Clubmeisterschaft)

import { useState, useEffect, useMemo } from "react";

/**
 * Generic setup wizard navigation hook.
 * @param {string} wizardMode - "simple" | "expert"
 * @param {Object} stepOk - { meta, players, time, rules, groupsko? }
 * @param {string} [storageKey] - localStorage key for mode (default: ttb:schleifchen:setupMode)
 * @param {string[]} [stepIds] - Optional step order (default: meta, players, time, rules). Use e.g. ["meta","players","groupsko","time","rules"] for Clubmeisterschaft.
 */
export function useSetupWizard(wizardMode, stepOk, storageKey = "ttb:schleifchen:setupMode", stepIds = null) {
  const WIZ_STEPS_ALL = useMemo(
    () => ({
      meta: { id: "meta", titleKey: "settings.wizard.step.meta.title", hintKey: "settings.wizard.step.meta.hint" },
      players: { id: "players", titleKey: "settings.wizard.step.players.title", hintKey: "settings.wizard.step.players.hint" },
      mode: { id: "mode", titleKey: "club.mode.step.title", hintKey: "club.mode.step.hint" },
      details: { id: "details", titleKey: "club.details.step.title", hintKey: "club.details.step.hint" },
      groupsko: { id: "groupsko", titleKey: "club.groupsko.step.title", hintKey: "club.groupsko.step.hint" },
      ko: { id: "ko", titleKey: "club.mode.ko.title", hintKey: "club.mode.ko.placeholder" },
      swiss: { id: "swiss", titleKey: "club.mode.swiss.title", hintKey: "club.mode.swiss.placeholder" },
      periods: { id: "periods", titleKey: "club.periods.step.title", hintKey: "club.periods.step.hint" },
      time: { id: "time", titleKey: "settings.wizard.step.time.title", hintKey: "settings.wizard.step.time.hint" },
      rules: { id: "rules", titleKey: "settings.wizard.step.rules.title", hintKey: "settings.wizard.step.rules.hint" },
      rules_cm: { id: "rules_cm", titleKey: "club.rules.step.title", hintKey: "club.rules.step.hint" },
      review: { id: "review", titleKey: "settings.wizard.step.review.title", hintKey: "settings.wizard.step.review.hint" },
    }),
    []
  );

  const defaultStepIds = ["meta", "players", "time", "rules"];
  const steps = useMemo(() => {
    const ids = Array.isArray(stepIds) && stepIds.length > 0 ? stepIds : defaultStepIds;
    return ids.map((id) => WIZ_STEPS_ALL[id]).filter(Boolean);
  }, [stepIds, WIZ_STEPS_ALL]);

  const [wizardStep, setWizardStep] = useState("meta");

  useEffect(() => {
    const ids = steps.map((s) => s.id);
    if (!ids.includes(wizardStep)) setWizardStep("meta");
  }, [steps, wizardStep]);

  useEffect(() => {
    try {
      if (storageKey) localStorage.setItem(storageKey, wizardMode);
    } catch (_) {}
  }, [wizardMode, storageKey]);

  const wizIdx = useMemo(() => steps.findIndex((s) => s.id === wizardStep), [steps, wizardStep]);

  const canGoNext = useMemo(() => {
    const cur = String(wizardStep || "");
    return !!stepOk?.[cur];
  }, [wizardStep, stepOk]);

  const progressPct = useMemo(() => {
    const total = Math.max(1, steps.length);
    const idx = Math.max(0, wizIdx);
    return Math.min(100, Math.max(0, Math.round(((idx + 1) / total) * 100)));
  }, [steps.length, wizIdx]);

  function wizPrev() {
    if (wizIdx <= 0) return;
    setWizardStep(steps[wizIdx - 1].id);
  }

  function wizNext() {
    if (wizIdx < 0 || wizIdx >= steps.length - 1) return;
    setWizardStep(steps[wizIdx + 1].id);
  }

  return {
    wizardStep,
    setWizardStep,
    steps,
    wizIdx,
    canGoNext,
    progressPct,
    wizPrev,
    wizNext,
  };
}
